import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { GlobalLoader } from "../../components";
import {
  resetUser,
  selectUser,
  setUser,
} from "../../core/data/redux/slice/user";
import { fetchProfile } from "../../service/auth";
import { loadBootstrapData } from "../../utils/bootstrapSession";
import { canAccess, canViewActivityLog, flattenPermissions, getLandingRoute, isSuperAdmin, isTeacher } from "../../utils/rbacHelper";
import AuthFeature from "../authFeature";
import Feature from "../feature";
import StudentDetailsLayout from "../peoples/students/student-details/StudentDetailsLayout";
import StudentDetails from "../peoples/students/student-details/studentDetails";
import { all_routes as routes } from "./all_routes";
import { authRoutes, publicRoutes } from "./router.link";
import StudentFees from "../peoples/students/student-details/studentFees";
import StudentResult from "../peoples/students/student-details/studentResult";
import StudentTimeTable from "../peoples/students/student-details/studentTimeTable";
import StudentLeaves from "../peoples/students/student-details/studentLeaves";
import StudentLibrary from "../peoples/students/student-details/studentLibrary";

interface AppRoute {
  path: string;
  element: JSX.Element;
  route: any;
  requiredPermission?: string;
  superAdminOnly?: boolean;
  ownerOnly?: boolean;
  // Also accessible to role "teacher" even without requiredPermission --
  // teachers carry zero RBAC permission rows by design (scoped by
  // TeacherAssignment ownership on the backend instead).
  teacherAllowed?: boolean;
}

const ALLRoutes: FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const user = useSelector(selectUser);
  const isLoggedIn = !!user._id;

  const handleGetProfile = async () => {
    setLoading(true);
    try {
      const userData = await fetchProfile();
      const permissions = flattenPermissions(userData.role.permissions);
      dispatch(setUser({ ...userData, permissions }));

      // Supplementary data (systems/sessions/branches) — in its own try/catch
      // so it structurally can't log the user back out, no matter what goes
      // wrong inside it. Only a failure of fetchProfile() itself (the actual
      // identity check) does that, in the outer catch block below. Retries
      // internally (see loadBootstrapData) if the branches call specifically
      // fails, since that endpoint is the slow/flaky one.
      try {
        await loadBootstrapData(dispatch, userData);
      } catch (bootstrapError) {
        console.error("Failed to load supplementary bootstrap data:", bootstrapError);
      }
    } catch (error) {
      console.error("Profile fetch failed:", error);
      dispatch(resetUser());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (window.location.pathname === routes.loginAsUser) {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");
      if (token) {
        try {
          sessionStorage.setItem("impersonationToken", token);
          sessionStorage.removeItem("currentBranchId");
          sessionStorage.removeItem("currentSessionId");
        } catch {
          /* ignore */
        }
        window.history.replaceState({}, document.title, routes.loginAsUser);
      }
    }

    handleGetProfile();
  }, []);

  const canAccessRoute = (route: AppRoute): boolean => {
    // Super-admin-only screens (e.g. Branch/Campus Management) — owner/admin only,
    // regardless of the permission grid.
    if (route.superAdminOnly) return isSuperAdmin(user);
    // Owner-only screens (Activity Log) — stricter still: not even admins,
    // and only the owners the backend names.
    if (route.ownerOnly) return canViewActivityLog(user);
    if (route.teacherAllowed && isTeacher(user)) return true;
    if (!route.requiredPermission) return true;

    if (!user.permissions || user.permissions.length === 0) {
      return true;
    }

    return canAccess(route.requiredPermission, user);
  };

  if (loading) return <GlobalLoader />;

  // Send the user to the first page they can actually access (a cashier without
  // "dashboard.view" would otherwise be bounced to /unauthorized).
  const landingRoute = getLandingRoute(user);

  return (
    <Routes>
      <Route
        path="/"
        element={
          isLoggedIn ? (
            <Navigate to={landingRoute} replace />
          ) : (
            <Navigate to={routes.login} replace />
          )
        }
      />

      <Route element={<AuthFeature />}>
        {publicRoutes.map((route, idx) => (
          <Route
            key={idx}
            path={route.path}
            element={
              isLoggedIn ? (
                <Navigate to={landingRoute} replace />
              ) : (
                route.element
              )
            }
          />
        ))}
      </Route>

      <Route
        element={
          isLoggedIn ? <Feature /> : <Navigate to={routes.login} replace />
        }
      >
        {(authRoutes as AppRoute[]).map((route, idx) => {
          const hasAccess = canAccessRoute(route);

          return (
            <Route
              key={idx}
              path={route.path}
              element={
                hasAccess ? (
                  route.element
                ) : (
                  <Navigate to="/unauthorized" replace />
                )
              }
            />
          );
        })}

        {/* Student details routes */}
        {canAccess("student.view", user) && (
          <Route
            path="/student/student-details/:id"
            element={<StudentDetailsLayout />}
          >
            <Route index element={<StudentDetails />} />
            {canAccess("students.view_fees", user) && (
              <Route path="fees" element={<StudentFees />} />
            )}
            {canAccess("students.view_results", user) && (
              <Route path="result" element={<StudentResult />} />
            )}
            {canAccess("students.view_timetable", user) && (
              <Route path="time-table" element={<StudentTimeTable />} />
            )}
            {canAccess("students.view_leaves", user) && (
              <Route path="leaves" element={<StudentLeaves />} />
            )}
            {canAccess("students.view_library", user) && (
              <Route path="library" element={<StudentLibrary />} />
            )}
          </Route>
        )}
      </Route>

      <Route
        path="*"
        element={
          isLoggedIn ? (
            <Navigate to={landingRoute} replace />
          ) : (
            <Navigate to={routes.login} replace />
          )
        }
      />
    </Routes>
  );
};

export default ALLRoutes;
